import {
  Paper,
  Title,
  Text,
  Stack,
  Button,
  Flex,
  Stepper,
  Card,
  Badge,
} from "@mantine/core";
import {
  InspectionsGet200ResponseRowsInner,
  InspectionsGetRequest,
  InspectionsGetRequestInspectionTypeEnum,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
  SpecificationsGet200ResponseRowsInner,
  SpcApiInspectionsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { ConfirmForm } from "@/features/ui/confirm";
import { useInspectForm } from "./hooks/useInspectForm";
import { useInspection } from "./InspectionContext";
import { ProcessInspectionExecution } from "./ProcessInspectionExecution";
import { useModal } from "@/context/ModalStackManager";
import { useWorkDataStore } from "@/store/work.store";
import { InspectSpecificationAutoComplete } from "@/components/organism/autoComplete/inspectSpecification/inspectSpecification-autoComplete";
import { inspectionSpecifications } from "@/api/inspect/useInspectQuery";
import { customNotification } from "@/utils/notificationShow";
import { useNotification } from "@/hooks/useNotification";

const FINAL_STEP_INDEX = 2;
const DEFAULT_SAMPLE_LABELS = [
  "LBL-0007",
  "LBL-0012",
  "LBL-0018",
  "LBL-0021",
  "LBL-0029",
];

const generateSampleLabels = (count: number) => {
  if (count <= 0) {
    return [];
  }
  if (count <= DEFAULT_SAMPLE_LABELS.length) {
    return DEFAULT_SAMPLE_LABELS.slice(0, count);
  }
  const extraCount = count - DEFAULT_SAMPLE_LABELS.length;
  const extras = Array.from({ length: extraCount }, (_value, index) => {
    const nextValue = 30 + index;
    return `LBL-${String(nextValue).padStart(4, "0")}`;
  });
  return [...DEFAULT_SAMPLE_LABELS, ...extras];
};

type LotInfoSnapshot = { name?: string | null; expiration?: string | null };

type ProcessTransactionValues = {
  itemCode?: string | null;
  locationCode?: string | null;
  quantity?: string | null;
  lotId?: number | null;
};

export interface TransactionWrapperProps {
  type?: "공정";
}

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("ko-KR");
};

export function TransactionWrapper({ type = "공정" }: TransactionWrapperProps) {
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));

  const queryClient = useQueryClient();
  const {
    setCurrentPlan,
    setInspectionUnits,
    setInspectionCriteria,
    setCurrentUnitIndex,
  } = useInspection();

  const { notifyInspectionChange } = useNotification();
  const { openModal, closeModal } = useModal();

  const [active, setActive] = useState(0);
  const [specificationPreview, setSpecificationPreview] =
    useState<SpecificationsGet200ResponseRowsInner | null>(null);
  const [isSpecificationPreviewLoading, setIsSpecificationPreviewLoading] =
    useState(false);
  const [activeInspectionSpecId, setActiveInspectionSpecId] = useState<
    number | null
  >(null);
  const previewRequestIdRef = useRef<number | null>(null);
  const [processInspection, setProcessInspection] =
    useState<InspectionsGet200ResponseRowsInner | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [inspectionCompleted, setInspectionCompleted] = useState(false);
  const [inspectionStatusMessage, setInspectionStatusMessage] = useState<
    string | null
  >(null);

  const processWork = workData.work ?? undefined;

  const processLot = processWork?.productionLot?.[0];
  const processLotInfo: LotInfoSnapshot = {
    name: processLot?.lotName ?? undefined,
    expiration: undefined,
  };
  const processLotId = processLot?.lotId;
  const processLocationCode =
    processWork?.targetLocationCode ??
    processWork?.locationSetting?.targetLocation?.code ??
    processWork?.locationSetting?.toLocation?.code ??
    undefined;
  const processQuantity = useMemo(() => {
    if (!processWork?.summary) return 0;
    const rawValue =
      processWork.summary.totalQuantity ??
      processWork.summary.performance ??
      processWork.summary.end ??
      "0";
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [processWork]);
  const transactionValues: ProcessTransactionValues = {
    itemCode: processWork?.item?.code,
    locationCode: processLocationCode,
    quantity: processQuantity > 0 ? String(processQuantity) : null,
    lotId: processLotId ?? null,
  };
  const transactionLotInfo = processLotInfo;
  const resolvedQuantity = processQuantity > 0 ? processQuantity : 0;
  const lotIdentifier = transactionValues.lotId
    ? String(transactionValues.lotId)
    : (transactionLotInfo?.name ?? "-");
  const inspectionLotId = processLotId;
  const inspectionTypeValue =
    InspectionsGetRequestInspectionTypeEnum.IN_PROCESS;

  const {
    specificationId,
    setSpecificationId,
    specificationPostData,
    createInspection,
    deleteInspection,
  } = useInspectForm({
    itemCode: transactionValues.itemCode ?? undefined,
    lotId: inspectionLotId,
    inspectionType: inspectionTypeValue,
    workId: processWork?.id,
  });

  const previewMatchesSelected = specificationPreview?.id === specificationId;
  const previewCriteria =
    previewMatchesSelected && specificationPreview?.criteria
      ? (specificationPreview.criteria as ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner[])
      : [];
  const responseMatchesSelectedSpecification =
    specificationPostData?.specificationId &&
    specificationPostData.specificationId === specificationId;
  const hasMatchingInspection =
    Boolean(specificationPostData) &&
    (activeInspectionSpecId === specificationId ||
      Boolean(responseMatchesSelectedSpecification));
  const hasCreatedInspection =
    Boolean(specificationPostData?.id) && hasMatchingInspection;
  const canPrepareInspectionPlan =
    Boolean(processWork?.id) &&
    Boolean(specificationId) &&
    (hasMatchingInspection || previewMatchesSelected) &&
    !isSpecificationPreviewLoading;
  const criteriaList =
    hasMatchingInspection && specificationPostData?.specificationData?.criteria
      ? specificationPostData.specificationData.criteria
      : previewCriteria;
  const derivedSampleCount = useMemo(() => {
    const sampleCounts = criteriaList
      .map((criteria) => criteria.sampleNum ?? 0)
      .filter((sampleNum) => typeof sampleNum === "number" && sampleNum > 0);
    if (sampleCounts.length === 0) {
      return 0;
    }
    return Math.max(...sampleCounts);
  }, [criteriaList]);
  const derivedSampleList = useMemo(
    () => generateSampleLabels(derivedSampleCount),
    [derivedSampleCount],
  );
  const resolvedSampleLabels = useMemo(() => {
    const planSampleList =
      derivedSampleList.length > 0 ? derivedSampleList : undefined;
    const fallbackSampleCount =
      planSampleList && planSampleList.length > 0
        ? planSampleList.length
        : derivedSampleCount || 1;
    const baseSampleLabels =
      planSampleList && planSampleList.length > 0
        ? planSampleList
        : generateSampleLabels(Math.max(fallbackSampleCount, 1));
    return baseSampleLabels.length > 0
      ? baseSampleLabels
      : [lotIdentifier || "SAMPLE-001"];
  }, [derivedSampleCount, derivedSampleList, lotIdentifier]);

  useEffect(() => {
    setInspectionCompleted(false);
    setInspectionStatusMessage(null);
  }, [specificationId]);

  const resetExecutionState = useCallback(() => {
    setIsExecuting(false);
    setProcessInspection(null);
    setCurrentPlan(null);
    setInspectionUnits([]);
    setInspectionCriteria([]);
    setCurrentUnitIndex(0);
  }, [
    setCurrentPlan,
    setCurrentUnitIndex,
    setInspectionCriteria,
    setInspectionUnits,
    setProcessInspection,
  ]);

  const handleExecutionCancel = useCallback(() => {
    setInspectionCompleted(false);
    setInspectionStatusMessage(null);
    resetExecutionState();
  }, [resetExecutionState]);

  const handleProcessExecutionComplete = useCallback(() => {
    setInspectionStatusMessage(
      "공정 검사를 완료했거나 이미 완료된 상태입니다.",
    );
    setInspectionCompleted(true);
    resetExecutionState();
  }, [resetExecutionState]);

  const loadSpecificationPreview = useCallback(
    async (targetSpecificationId: number) => {
      if (!targetSpecificationId) {
        previewRequestIdRef.current = null;
        setSpecificationPreview(null);
        return;
      }
      previewRequestIdRef.current = targetSpecificationId;
      setIsSpecificationPreviewLoading(true);
      try {
        const queryOptions = inspectionSpecifications.detail({
          specificationId: targetSpecificationId,
        });
        const response = await queryClient.fetchQuery(queryOptions);
        if (previewRequestIdRef.current === targetSpecificationId) {
          setSpecificationPreview(response.data ?? null);
        }
      } catch (error) {
        if (previewRequestIdRef.current !== targetSpecificationId) {
          return;
        }
        setSpecificationPreview(null);
        customNotification.error({
          message: "검사 기준 정보를 불러오지 못했습니다.",
        });
      } finally {
        setIsSpecificationPreviewLoading(false);
      }
    },
    [queryClient],
  );

  const goToNextStep = () => {
    setActive((current) => Math.min(current + 1, FINAL_STEP_INDEX));
  };

  const goToPreviousStep = () => {
    setActive((current) => Math.max(current - 1, 0));
  };

  const handleStepClick = (step: number) => {
    setActive(step);
  };

  const buildInspectionPayload = useCallback(
    (nextSpecificationId: number): SpcApiInspectionsPostRequest => {
      const sampleQuantity =
        resolvedSampleLabels.length > 0 ? resolvedSampleLabels.length : 1;
      const contextUnits =
        resolvedSampleLabels.length > 0
          ? resolvedSampleLabels.map((label, index) => ({
              unitIndex: index + 1,
              unitCode: label,
            }))
          : undefined;
      const inspectionsGetRequest: InspectionsGetRequest & {
        inspectionMethod?: "FULL" | "SAMPLE";
      } = {
        itemCode: transactionValues.itemCode ?? undefined,
        lotId: inspectionLotId,
        inspectionType: inspectionTypeValue,
        specificationId: nextSpecificationId,
        inspectionMethod: "SAMPLE",
        context: contextUnits ? { units: contextUnits } : undefined,
        targetQuantity: String(sampleQuantity),
      };
      if (processWork?.id) {
        inspectionsGetRequest.workId = processWork.id;
        inspectionsGetRequest.productionPlanId = processWork.productionPlanId;
      }
      return {
        inspectionsGetRequest,
      };
    },
    [
      transactionValues.itemCode,
      inspectionLotId,
      inspectionTypeValue,
      resolvedSampleLabels,
      processWork?.id,
      processWork?.productionPlanId,
    ],
  );

  const requestInspectionCreation = useCallback(
    (
      targetSpecificationId: number,
      _methodType: "FULL" | "SAMPLE",
      options?: Parameters<typeof createInspection>[1],
    ) => {
      if (!targetSpecificationId || !transactionValues.itemCode) return;
      createInspection(buildInspectionPayload(targetSpecificationId), options);
    },
    [buildInspectionPayload, createInspection, transactionValues.itemCode],
  );

  const handleCriteriaStepNext = () => {
    if (!canPrepareInspectionPlan) {
      return;
    }
    goToNextStep();
  };

  const handleMethodStepNext = () => {
    if (!processWork?.id) {
      return;
    }
    if (hasCreatedInspection) {
      goToNextStep();
      return;
    }
    if (!canPrepareInspectionPlan) {
      return;
    }
    const targetSpecificationId = specificationId;
    requestInspectionCreation(targetSpecificationId, "SAMPLE", {
      onSuccess: () => {
        setSpecificationPreview(null);
        setActiveInspectionSpecId(targetSpecificationId);
        goToNextStep();
      },
    });
  };

  const handleSpecificationChange = useCallback(
    async (_value: string | null, option?: { id: number } | null) => {
      const nextId = option?.id ? Number(option.id) : 0;
      const resetPreview = () => setSpecificationPreview(null);
      const loadPreview = () => {
        resetPreview();
        if (nextId) {
          void loadSpecificationPreview(nextId);
        }
      };
      if (!nextId) {
        previewRequestIdRef.current = null;
        setActiveInspectionSpecId(null);
        resetPreview();
        setSpecificationId(0);
        return;
      }

      if (
        specificationId === nextId &&
        (specificationPostData || specificationPreview)
      ) {
        return;
      }
      if (specificationId !== nextId) {
        setActiveInspectionSpecId(null);
      }

      const currentInspectionId = specificationPostData?.id ?? 0;
      if (
        currentInspectionId &&
        specificationId !== 0 &&
        nextId !== specificationId
      ) {
        const inspectionChangeConfirm = await notifyInspectionChange();

        if (inspectionChangeConfirm) {
          const payloadId = currentInspectionId;
          setActiveInspectionSpecId(null);
          deleteInspection(
            { inspectionId: payloadId },
            {
              onSuccess: () => {
                setSpecificationId(nextId);
                loadPreview();
              },
            },
          );
        }

        return;
      }

      setSpecificationId(nextId);
      loadPreview();
    },
    [
      closeModal,
      deleteInspection,
      loadSpecificationPreview,
      openModal,
      specificationId,
      specificationPostData,
      specificationPostData?.id,
      specificationPreview,
      setSpecificationId,
    ],
  );

  const handleCreateInspection = () => {
    if (!processWork?.id) {
      return;
    }
    if (!hasCreatedInspection || !specificationPostData) {
      return;
    }
    if (inspectionCompleted) {
      setInspectionStatusMessage(
        "공정 검사를 완료했거나 이미 완료된 상태입니다.",
      );
      closeModal(null);
      return;
    }
    setInspectionStatusMessage(null);
    setProcessInspection(specificationPostData);
    setIsExecuting(true);
  };

  const finalStatusMessage = inspectionStatusMessage;
  const confirmDisabled = !hasCreatedInspection || !processWork?.id;
  const formattedLotExpiration = formatDate(transactionLotInfo?.expiration);

  const getCriteriaBadge = (
    type?: ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner["type"],
  ) => {
    switch (type) {
      case "number":
        return { label: "수치", color: "blue" as const };
      case "boolean":
        return { label: "합/불", color: "green" as const };
      case "select":
        return { label: "선택", color: "grape" as const };
      case "photo":
        return { label: "사진", color: "cyan" as const };
      default:
        return { label: "기타", color: "gray" as const };
    }
  };

  const getCriteriaSummary = (
    criteria: ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
  ) => {
    if (criteria.type === "number") {
      const min = criteria.min ?? "-";
      const max = criteria.max ?? "-";
      return `${min} ~ ${max}`;
    }
    if (criteria.type === "select") {
      const options = Array.isArray(criteria.options) ? criteria.options : [];
      return options.filter(Boolean).join(", ") || "-";
    }
    if (criteria.type === "boolean") {
      return criteria.standard ? "체크시 합격" : "체크시 불합격";
    }
    if (criteria.type === "photo") {
      return "사진 업로드 필요";
    }
    return (criteria.standard as string) ?? "-";
  };

  if (isExecuting && processInspection) {
    return (
      <ProcessInspectionExecution
        inspection={processInspection}
        onCancel={handleExecutionCancel}
        onComplete={handleProcessExecutionComplete}
      />
    );
  }

  return (
    <Stack spacing="lg" w="1300px">
      <Flex justify="space-between">
        <Title order={4}>{`${type}/검사 생성`}</Title>
      </Flex>

      {!processWork?.id && (
        <Card withBorder padding="md" radius="md">
          <Stack spacing="xs">
            <Text fw={600}>공정 데이터를 선택해주세요</Text>
            <Text size="sm" c="dimmed">
              공정 정보를 확인할 수 없는 상태입니다. 검사할 작업을 먼저 지정해야
              합니다.
            </Text>
          </Stack>
        </Card>
      )}

      <Paper p="lg" withBorder>
        <Stepper active={active} onStepClick={handleStepClick}>
          <Stepper.Step label="기준 선택" description="검사 기준">
            <Stack spacing="lg" mt="xl">
              <InspectSpecificationAutoComplete
                label="검사 기준"
                placeholder={
                  transactionValues.itemCode
                    ? "검사 기준을 선택하세요"
                    : "품목을 먼저 선택하세요"
                }
                inspectionType={inspectionTypeValue}
                itemCode={transactionValues.itemCode ?? undefined}
                disabled={!transactionValues.itemCode}
                onChange={handleSpecificationChange}
              />

              <div>
                <Text size="sm" fw={600} mb="sm">
                  검사 항목 미리보기
                </Text>
                {isSpecificationPreviewLoading ? (
                  <Card withBorder padding="md">
                    <Text size="sm" c="dimmed">
                      검사 기준 정보를 불러오는 중입니다.
                    </Text>
                  </Card>
                ) : criteriaList.length === 0 ? (
                  <Card withBorder padding="md">
                    <Text size="sm" c="dimmed">
                      검사 기준을 선택하면 항목이 표시됩니다.
                    </Text>
                  </Card>
                ) : (
                  <Stack spacing="xs">
                    {criteriaList.map((criteria) => {
                      const badge = getCriteriaBadge(criteria.type);
                      return (
                        <Card
                          key={`${criteria.name}-${criteria.type}`}
                          withBorder
                          padding="sm"
                        >
                          <Flex justify="space-between" align="flex-start">
                            <div>
                              <Text size="sm" fw={500}>
                                {criteria.name}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {getCriteriaSummary(criteria)}
                              </Text>
                              {criteria.sampleNum ? (
                                <Text size="xs" c="dimmed">
                                  샘플 {criteria.sampleNum}개
                                </Text>
                              ) : null}
                            </div>
                            <Badge
                              size="sm"
                              variant="light"
                              color={badge.color}
                            >
                              {badge.label}
                            </Badge>
                          </Flex>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </div>

              <Flex justify="flex-end">
                <Button
                  onClick={handleCriteriaStepNext}
                  disabled={!canPrepareInspectionPlan}
                >
                  다음
                </Button>
              </Flex>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="방법/샘플링" description="검사 방법">
            <Stack spacing="lg" mt="xl">
              <Card withBorder style={{ background: "#eff6ff" }}>
                <Stack spacing="xs">
                  <Flex gap="sm">
                    <Badge color="blue" variant="light">
                      공정 전용
                    </Badge>
                    <Text size="sm" fw={600}>
                      샘플 검사
                    </Text>
                  </Flex>
                  <Text size="sm" c="dimmed">
                    공정 검사는 샘플 검사만 지원하며 기준에서 정의된 샘플 수를
                    사용합니다.
                  </Text>
                  <Flex gap="lg">
                    <Text size="sm">
                      샘플 수: {resolvedSampleLabels.length || 0}개
                    </Text>
                    <Text size="sm" c="dimmed">
                      LOT: {lotIdentifier}
                    </Text>
                  </Flex>
                </Stack>
              </Card>

              <Flex justify="space-between">
                <Button variant="outline" onClick={goToPreviousStep}>
                  이전
                </Button>
                <Button
                  onClick={handleMethodStepNext}
                  disabled={!canPrepareInspectionPlan}
                >
                  다음
                </Button>
              </Flex>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="확인" description="최종 확인">
            <Stack spacing="lg" mt="xl">
              <Card withBorder padding="lg">
                <Stack spacing="md">
                  <Text size="lg" fw={600}>
                    최종 확인
                  </Text>

                  <Flex gap="xl">
                    <div>
                      <Text size="sm" c="dimmed">
                        범위
                      </Text>
                      <Text size="sm" fw={500}>
                        {type}
                      </Text>
                    </div>
                    <div>
                      <Text size="sm" c="dimmed">
                        검사 방법
                      </Text>
                      <Text size="sm" fw={500}>
                        샘플 검사
                      </Text>
                    </div>
                  </Flex>

                  <Stack spacing="sm">
                    <Text size="md" fw={600}>
                      공정 정보
                    </Text>
                    <Flex gap="xl">
                      <div>
                        <Text size="sm" c="dimmed">
                          품목
                        </Text>
                        <Text size="sm" fw={500}>
                          {transactionValues.itemCode ?? "-"}
                        </Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">
                          로케이션
                        </Text>
                        <Text size="sm" fw={500}>
                          {transactionValues.locationCode ?? "-"}
                        </Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">
                          목표 수량
                        </Text>
                        <Text size="sm" fw={500}>
                          {resolvedQuantity || "-"}
                        </Text>
                      </div>
                    </Flex>
                  </Stack>

                  <Flex gap="xl">
                    <div>
                      <Text size="sm" c="dimmed">
                        LOT
                      </Text>
                      <Text size="sm" fw={500}>
                        {lotIdentifier}
                      </Text>
                    </div>
                    <div>
                      <Text size="sm" c="dimmed">
                        LOT 만료일
                      </Text>
                      <Text size="sm" fw={500}>
                        {formattedLotExpiration}
                      </Text>
                    </div>
                  </Flex>
                  {finalStatusMessage && (
                    <Text size="sm" c="red" fw={500}>
                      {finalStatusMessage}
                    </Text>
                  )}
                </Stack>
              </Card>

              <Flex justify="space-between">
                <Button variant="outline" onClick={goToPreviousStep}>
                  이전
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreateInspection}
                  disabled={confirmDisabled}
                >
                  검사 실행
                </Button>
              </Flex>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Paper>
    </Stack>
  );
}
